import { FC, PropsWithChildren, ReactElement, useState } from 'react';
import { useMeasure } from 'react-use';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Divider, Typography } from '@mui/material';
import { animated, useSpring } from '@react-spring/web';

import useStyles from './styles';

type Props = {
  iconText: string;
  question: string | ReactElement;
  description: string | ReactElement;
};

const FAQItem: FC<PropsWithChildren<Props>> = ({
  iconText,
  question,
  description,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [ref, { height }] = useMeasure();
  const { classes, cx } = useStyles();

  const expand = useSpring({
    config: { friction: 50, duration: 300 },
    // NOTE: adjusting for marginBottom 12px of titleContainer when the description is hidden
    height: isOpen ? height : height - 12,
  });

  const opacity = useSpring({
    config: { duration: 300 },
    height: isOpen ? 'auto' : 0,
    opacity: isOpen ? 1 : 0,
    display: isOpen ? 'block' : 'none',
  });

  const handleClick = () => setIsOpen((prev) => !prev);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleClick();
    }
  };

  return (
    <animated.div style={expand} className={classes.faqItem}>
      <div ref={ref}>
        <Divider className={classes.divider} />
        <div className={classes.contentContainer}>
          <Typography
            variant="title2Xs"
            component="span"
            className={classes.titleIcon}
          >
            {iconText}
          </Typography>
          <div className={classes.container}>
            <div
              className={classes.titleContainer}
              onClick={handleClick}
              onKeyPress={handleKeyPress}
              role="button"
              tabIndex={0}
            >
              <Typography
                variant="title2Xs"
                component="h3"
                className={classes.title}
              >
                {question}
              </Typography>
              <KeyboardArrowDownIcon
                className={cx(classes.icon, {
                  [classes.iconAnimationClose]: !isOpen,
                  [classes.iconAnimationOpen]: isOpen,
                })}
              />
            </div>
            <animated.div style={opacity}>
              <Typography
                variant="bodyLg"
                component="p"
                className={classes.description}
              >
                {description}
              </Typography>
            </animated.div>
          </div>
        </div>
      </div>
    </animated.div>
  );
};

export default FAQItem;
